import { booking } from '../api/booking';

/**
 * Where a row of the client's orders opens: its delivery's tracking screen.
 * One rule for every list, so no screen can send a delivery to a page that
 * cannot read it (the profile's sent InGo deliveries to the old order page,
 * until 2 Oct 2026).
 * @param {{ apiDeliveryId?: string|number }} order
 */
export function orderPath(order) {
  return `/live-tracking?delivery=${encodeURIComponent(order?.apiDeliveryId ?? '')}`;
}

function formatListDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(iso);
  }
}

/** InGo's statuses as the order list's four (active, transit, delivered, cancelled). */
const API_LIST_STATUS = {
  requested: 'active',
  offered: 'active',
  counter_bid: 'active',
  accepted: 'active',
  picked_up: 'transit',
  delivered: 'delivered',
  completed: 'delivered',
  cancelled: 'cancelled',
};

/**
 * The signed-in client's deliveries from InGo's API (M5,
 * `customer/me/deliveries`), as the order list's rows. A row opens InGo's
 * tracking (`apiDeliveryId`).
 * @param {object | null} session The client's session; without one there is nothing to ask for.
 */
export async function fetchCustomerUnifiedOrders(session) {
  if (!session) return { orders: [], error: 'Sign in to see your orders.' };
  try {
    const deliveries = await booking.deliveries();

    return {
      orders: deliveries.map((d) => {
        const status = API_LIST_STATUS[d.status] || 'active';

        return {
          navKey: `api:${d.id}`,
          apiDeliveryId: d.id,
          kind: 'delivery',
          id: `Parcel · ${d.reference}`,
          status,
          from: d.pickup?.address || '—',
          to: d.dropoff?.address || '—',
          date: formatListDate(d.requested_at),
          price: new Intl.NumberFormat('en-US', { style: 'currency', currency: d.price?.currency || 'USD' }).format((d.price?.total_minor || 0) / 100),
          sortAt: d.requested_at,
          subtitle: 'Parcel delivery',
          canCancel: Boolean(d.may_cancel),
          awaitingDriver: ['requested', 'offered', 'counter_bid'].includes(d.status),
          statusText: d.status_label,
          deliveryConfirmationCode: status === 'delivered' || status === 'cancelled' ? null : d.code || null,
          cancelReason: d.cancel_reason || null,
          cancelledBy: d.cancelled_by || null,
        };
      }),
      error: null,
    };
  } catch (e) {
    return { orders: [], error: e?.message || 'Your orders could not be loaded.' };
  }
}
